/* eslint-disable no-nested-ternary */
import React, { useEffect, useRef, useState } from 'react';
import { Message, Tab, MenuItem, Label } from 'semantic-ui-react';
import { useSelector, useDispatch } from 'react-redux';
import { isEmpty, omit } from 'lodash';
import queryString from 'query-string';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import Toolbar from 'components/common/Toolbar';
import Loader from 'components/common/PageLoader';
import ToolbarFooter from 'components/common/Toolbar/ToolbarFooter';
import LoanRequestTable from 'containers/MicroLoanRequests/LoanRequestTable';
import Empty from 'components/common/Empty';
import './index.scss';
import getLoanRequests from 'redux/actions/loanRequests/getLoanRequests';
import SidebarFilter from 'components/common/SidebarFilter';
import { formatSearchParameter } from 'helpers/formatUrlParameter';
import getProfileOverview from 'redux/actions/organizations/profiles/profileOverview';
import getCurrentOrg from 'helpers/getCurrentOrg';
import SingleLoanRequest from './SingleRequest';

const ProfileRequestList = () => {
  const params = useParams();
  const { search, pathname } = useLocation();
  const { push } = useHistory();
  const urlParams = new URLSearchParams(search);
  const queryParams = Object.fromEntries(urlParams);
  const [filterOn, setFilterOn] = useState(false);
  const [filterForm, setFilterForm] = useState({});
  const [activeTab, setActiveTab] = useState('submitted');
  const abortControllerRef = useRef(null);
  const {
    organizationId,
    profileId: theProfileId,
    tabOption,
  } = params;
  const profileId = theProfileId || tabOption;

  const { items, meta, loading, error } = useSelector(
    ({ loanRequests: { pendingLoanRequests } }) =>
      pendingLoanRequests,
  );
  const {
    data: { profile },
  } = useSelector(
    ({ profiles: { profileOverview } }) => profileOverview,
  );

  const dispatch = useDispatch();
  const currentUserOrganization = getCurrentOrg();

  useEffect(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    getLoanRequests(
      {
        organization_profile_id: profileId,
        ...queryParams,
        status: activeTab,
      },
      { signal: controller.signal },
    )(dispatch);

    return () => {
      controller.abort();
    };
  }, [profileId, filterForm, activeTab]);

  useEffect(() => {
    if (
      (organizationId || currentUserOrganization?.id) &&
      profileId
    ) {
      getProfileOverview(
        organizationId || currentUserOrganization?.id,
        profileId,
      )(dispatch);
    }
  }, [organizationId, currentUserOrganization, profileId]);

  const getRequests = (
    profileId,
    queries = queryString.parse(search),
  ) => {
    return getLoanRequests({
      organization_profile_id: profileId,
      ...queries,
      status: activeTab,
    })(dispatch);
  };

  const onSearch = queries => {
    getRequests(profileId, queries);
  };

  const onPaginate = queries => {
    getRequests(profileId, queries);
  };

  const defaultValue = {
    group_type: filterForm?.group_type,
    request_type: filterForm?.request_type,
    status: filterForm?.status,
    purpose: filterForm?.purpose,
  };

  const handleChange = (e, { value, name }) => {
    if (name === 'request_type')
      push(
        formatSearchParameter({
          ...omit(defaultValue, 'request_type'),
          [name]: value,
        }),
      );
    if (name === 'status')
      push(
        formatSearchParameter({
          ...omit(defaultValue, 'status'),
          [name]: value,
        }),
      );
    if (name === 'group_type')
      push(
        formatSearchParameter({
          ...omit(defaultValue, 'group_type'),
          [name]: value,
        }),
      );
    if (name === 'purpose')
      push(
        formatSearchParameter({
          ...omit(defaultValue, 'purpose'),
          [name]: value,
        }),
      );
    setFilterForm({ ...filterForm, [name]: value });
  };

  if (
    params.organizationId &&
    params.tab === 'requests' &&
    params.tabOption
  ) {
    return <SingleLoanRequest />;
  }

  const panes = () => [
    {
      menuItem: (
        <MenuItem key="submitted">
          Pending Approval{' '}
          {(meta?.submitted || '') && (
            <Label color="red">{meta?.submitted}</Label>
          )}
        </MenuItem>
      ),
    },
    {
      menuItem: (
        <MenuItem key="approved">
          Approved{' '}
          {(meta?.approved || '') && (
            <Label
              style={{
                backgroundColor: '#F9FAFF',
                color: '#323652',
                border: '2px solid #484E730F',
              }}
            >
              {meta?.approved}
            </Label>
          )}
        </MenuItem>
      ),
    },
    {
      menuItem: (
        <MenuItem key="rejected">
          Rejected{' '}
          {(meta?.rejected || '') && (
            <Label
              style={{
                backgroundColor: '#F9FAFF',
                color: '#323652',
                border: '2px solid #484E730F',
              }}
            >
              {meta?.rejected}
            </Label>
          )}
        </MenuItem>
      ),
    },
    {
      menuItem: (
        <MenuItem key="expired">
          Expired{' '}
          {(meta?.expired || '') && (
            <Label
              style={{
                backgroundColor: '#F9FAFF',
                color: '#323652',
                border: '2px solid #484E730F',
              }}
            >
              {meta?.expired}
            </Label>
          )}
        </MenuItem>
      ),
    },
  ];

  const tabs = ['submitted', 'approved', 'rejected', 'expired'];

  const handleTabChange = (e, { activeIndex }) => {
    const tab = tabs[activeIndex];
    setActiveTab(tab);
  };

  const activeIndex = tabs.findIndex(item => item === activeTab) || 0;

  return (
    <>
      <div className="profile-requests__lists">
        <Toolbar
          header={{
            title:
              profile && pathname.includes('/profile')
                ? `${profile.name} > Micro Loans`
                : 'Micro Loans',

            navigation: true,
            rightComponent: (
              <ToolbarFooter
                onSearch={onSearch}
                onPaginate={onPaginate}
                paginationMeta={meta}
                filter
                filterClicked={() => setFilterOn(true)}
                exportCSVData={items}
                exportCSVFileName="microloan-requests"
              />
            ),
          }}
        />

        <div
          className="list-profiles__items scroll-wrapper"
          style={{
            paddingTop: 0,
          }}
        >
          <Tab
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            }}
            activeIndex={activeIndex}
            menu={{ secondary: true, pointing: true }}
            panes={panes()}
            onTabChange={handleTabChange}
          />
          {loading ? (
            <Loader />
          ) : isEmpty(items) ? (
            <Empty
              message={`No ${tabs[activeIndex] || 'pending'} request`}
            />
          ) : error ? (
            <Message negative>{error?.message || error}</Message>
          ) : (
            <LoanRequestTable activeTab={activeTab} items={items} />
          )}
        </div>
      </div>
      <SidebarFilter
        filterOn={filterOn}
        setFilterOn={setFilterOn}
        handleChange={handleChange}
        form={filterForm}
        setForm={setFilterForm}
        activity="microloanRequests"
      />
    </>
  );
};

export default ProfileRequestList;
